/**
 * TagsFilter
 * Refatorado: Usa hooks utilitários useClickOutside e useDropdownPosition.
 */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AppIcon } from '../../icons/AppIcon';
import { Button } from '../../ui/Button';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { useDropdownPosition } from '../../../hooks/useDropdownPosition';
import { cnFilterChip } from './filterChipStyles';

interface Tag {
    id: string;
    name: string;
    color?: string;
}

interface TagsFilterProps {
    tags: Tag[];
    selectedTags: string[];
    onApply: (tags: string[]) => void;
}

export const TagsFilter: React.FC<TagsFilterProps> = ({ tags, selectedTags, onApply }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempTags, setTempTags] = useState<string[]>(selectedTags);

    const triggerRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Hooks utilitários
    const { position, calculatePosition } = useDropdownPosition(triggerRef, {
        offsetY: 4,
        panelWidth: 256,
        viewportPadding: 16,
    });
    useClickOutside(dropdownRef, () => setIsOpen(false), isOpen, [triggerRef]);

    // Sincroniza seleção quando abre ou quando muda externamente
    useEffect(() => {
        setTempTags(selectedTags);
    }, [isOpen, selectedTags]);

    const toggleDropdown = () => {
        if (!isOpen) {
            calculatePosition();
        }
        setIsOpen(!isOpen);
    };

    const toggleTag = (id: string) => {
        setTempTags(prev =>
            prev.includes(id)
                ? prev.filter(t => t !== id)
                : [...prev, id]
        );
    };

    const handleClear = () => {
        onApply([]);
        setIsOpen(false);
    };

    const handleApply = () => {
        onApply(tempTags);
        setIsOpen(false);
    };

    const hasSelection = selectedTags.length > 0;

    const dropdown = (
        <div
            ref={dropdownRef}
            role="dialog"
            aria-label="Filtrar por tags"
            className="fixed z-[9999] w-[min(16rem,calc(100vw-2rem))] rounded-xl border border-border bg-background p-3 shadow-xl dark:bg-surface-subtle"
            style={{
                top: position.top,
                left: position.left,
            }}
        >
            <div className="flex items-center justify-between mb-2 px-1">
                <span className="font-semibold text-default text-sm">Tags</span>
                <span className="text-xs text-secondary">{tags.length} opções</span>
            </div>

            <div className="max-h-60 overflow-y-auto space-y-1 custom-scrollbar mb-3">
                {tags.length > 0 ? (
                    tags.map(tag => (
                        <label key={tag.id} className="flex items-center space-x-3 cursor-pointer p-2 hover:bg-surface-subtle dark:hover:bg-surface rounded-lg transition-colors group">
                            <input
                                id={`tag-filter-${tag.id}`}
                                name={`tag-filter-${tag.id}`}
                                type="checkbox"
                                checked={tempTags.includes(tag.id)}
                                onChange={() => toggleTag(tag.id)}
                                className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary transition-colors cursor-pointer"
                            />
                            <div className="flex items-center gap-2 flex-1">
                                <div
                                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: tag.color || '#cbd5e1' }}
                                />
                                <span className="text-sm text-secondary group-hover:text-text transition-colors">{tag.name}</span>
                            </div>
                        </label>
                    ))
                ) : (
                    <p className="text-xs text-muted italic p-2 text-center">Nenhuma tag disponível</p>
                )}
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-border-subtle pt-3">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleClear}
                    className="min-h-11 text-sm font-medium text-secondary hover:bg-surface-subtle hover:text-default md:min-h-0"
                >
                    Limpar
                </Button>
                <Button
                    type="button"
                    variant="default"
                    size="sm"
                    onClick={handleApply}
                    className="min-h-11 px-4 text-sm font-medium md:min-h-0"
                >
                    Aplicar filtro
                </Button>
            </div>
        </div>
    );

    return (
        <>
            {/* Trigger Button */}
            <button
                ref={triggerRef}
                onClick={toggleDropdown}
                type="button"
                aria-expanded={isOpen}
                aria-haspopup="dialog"
                className={cnFilterChip(hasSelection || isOpen)}
            >
                <AppIcon name="tag" className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="min-w-0 truncate">
                    {hasSelection ? `${selectedTags.length}` : 'Tags'}
                    <span className="hidden md:inline">{hasSelection ? ' tags' : ''}</span>
                </span>
                <AppIcon name="chevron-down" className="hidden h-3 w-3 shrink-0 opacity-70 md:block" aria-hidden />
            </button>

            {/* Portal Dropdown */}
            {isOpen && createPortal(dropdown, document.body)}
        </>
    );
};
